// Shared helpers for resource configs (base configs + crud/overrides/*).
import { CrudRecord } from './types';
import { t } from '../i18n';

export const yesNo = [
  { label: t('status.no'), value: 0 },
  { label: t('status.yes'), value: 1 }
];

export function boolFlags(payload: Record<string, unknown>, fields: string[]) {
  return fields.reduce(
    (next, field) => ({
      ...next,
      [field]: payload[field] ? 1 : 0
    }),
    { ...payload }
  );
}

export function relationIds(value: unknown): Array<string | number> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const id = (item as Record<string, unknown>).id ?? (item as Record<string, unknown>).value;
    return id == null ? [] : [id as string | number];
  });
}

export function deploymentCategoryIds(value: unknown): Array<string | number> {
  if (!Array.isArray(value)) return [];
  const ids = value.map(String).filter(Boolean);
  if (ids.includes('all')) return [];
  return Array.from(new Set(ids));
}

export function normalizeItemAfterLoad(record: CrudRecord): CrudRecord {
  const categories = relationIds(record.categories);
  return {
    ...record,
    category_id: record.category_id ?? categories[0] ?? '',
    taxes: relationIds(record.taxes),
    modifiers: relationIds(record.modifiers)
  };
}

export function normalizePromotionAfterLoad(record: CrudRecord): CrudRecord {
  return {
    ...record,
    categories: relationIds(record.categories)
  };
}

// Converts a form variation row into the legacy `variations[]` shape and enforces
// the stock-safety guardrails: quantity is forced to 0 and any stock[] sub-array is
// stripped, so variations never declare opening inventory from this form.
// `meta` is collected as text like "Size:M,Color:Red" and parsed into [{Size:"M"},{Color:"Red"}].
export function parseVariationMeta(variation: Record<string, unknown>): {
  sku: string;
  code?: string;
  cost: number;
  price: number;
  meta: Array<Record<string, string>>;
  quantity: 0;
  stock: never[];
} {
  const rawMeta = String(variation.meta ?? '').trim();
  const meta = rawMeta
    .split(',')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const sep = pair.indexOf(':');
      if (sep <= 0) return null;
      return { [pair.slice(0, sep).trim()]: pair.slice(sep + 1).trim() } as Record<string, string>;
    })
    .filter((entry): entry is Record<string, string> => entry !== null);
  return {
    sku: String(variation.sku ?? ''),
    code: variation.code ? String(variation.code) : undefined,
    cost: Number(variation.cost ?? 0),
    price: Number(variation.price ?? 0),
    meta,
    quantity: 0,
    stock: []
  };
}

export function normalizeItemBeforeSave(payload: Record<string, unknown>) {
  const next = boolFlags(payload, ['changeable', 'is_service', 'tax_included', 'expiry', 'is_stock', 'has_variants', 'has_serials', 'hide_in_pos', 'hide_in_shop']);
  return {
    ...next,
    type: next.type || 'standard',
    symbology: next.symbology || 'code128',
    taxes: Array.isArray(next.taxes) ? next.taxes : [],
    modifiers: Array.isArray(next.modifiers) ? next.modifiers : [],
    stock: Array.isArray(next.stock) ? next.stock : [],
    variants: Array.isArray(next.variants) ? next.variants : [],
    variations: (Array.isArray(next.variations) ? next.variations : [])
      .map((variation) => parseVariationMeta(variation as Record<string, unknown>))
      .filter((variation) => variation.sku && Array.isArray(variation.meta) && variation.meta.length > 0),
    portions: Array.isArray(next.portions) ? next.portions : [],
    serials: Array.isArray(next.serials) ? next.serials : []
  };
}

// Wrap normalizeItemBeforeSave to force a specific item type (service, recipe,
// combo). Used by the sub-type resource configs so that items created from the
// Services/Recipes/Combos pages always have the correct type, regardless of
// what the form sends.
export function normalizeItemOfType(payload: Record<string, unknown>, type: string) {
  return { ...normalizeItemBeforeSave({ ...payload, type }) };
}

// SAFETY GUARDRAIL for transactions: sales and purchases are ALWAYS draft from
// this form. A non-draft transaction deducts/adds stock, posts journals, creates
// costings, mutates loyalty/gift-cards, and — with a payment — becomes
// non-deletable. Drafts write only the header + line rows and delete cleanly.
// We strip payment/pos/register keys (defense-in-depth) and force draft: true.
export function normalizeDraftOrderBeforeSave(payload: Record<string, unknown>, priceField: 'price' | 'cost') {
  const next = { ...payload };
  return {
    type: next.type || 'sale',
    date: next.date || new Date().toISOString().slice(0, 10),
    // The party field differs: sales use customer_id, purchases use supplier_id.
    ...(next.customer_id ? { customer_id: next.customer_id } : {}),
    ...(next.supplier_id ? { supplier_id: next.supplier_id } : {}),
    // HARD GUARDRAIL: never finalize, never pay, never use POS context.
    draft: 1,
    items: (Array.isArray(next.items) ? next.items : [])
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const row = item as Record<string, unknown>;
        return {
          item_id: row.item_id,
          code: row.code ?? '',
          name: row.name ?? '',
          [priceField]: Number(row[priceField] ?? 0),
          quantity: Number(row.quantity ?? 0),
          ...(row.unit_cost != null ? { unit_cost: Number(row.unit_cost) } : {}),
          ...(row.unit_price != null ? { unit_price: Number(row.unit_price) } : {})
        };
      })
      .filter((row) => row.item_id && Number(row.quantity) > 0)
  };
}

export function normalizeSaleBeforeSave(payload: Record<string, unknown>) {
  return normalizeDraftOrderBeforeSave(payload, 'price');
}

// Stock Adjustments: draft-gated (draft=1). The AdjustmentEventListner skips
// the stock mutation when draft is truthy. Items carry code/name/quantity/cost.
export function normalizeAdjustmentBeforeSave(payload: Record<string, unknown>) {
  const next = { ...payload };
  return {
    type: next.type || 'addition',
    date: next.date || new Date().toISOString().slice(0, 10),
    reference: next.reference ?? '',
    details: next.details ?? '',
    // HARD GUARDRAIL: normal form saves are always drafts. The only deliberate
    // exception is finalizeRecord(), which passes draft=0 explicitly to post
    // the stock movement.
    draft: next.draft === 0 ? 0 : 1,
    items: (Array.isArray(next.items) ? next.items : [])
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const row = item as Record<string, unknown>;
        const taxIds = (Array.isArray(row.taxes) ? row.taxes : [])
          .map((tax) => {
            if (tax && typeof tax === 'object') {
              return (tax as Record<string, unknown>).id;
            }
            return tax;
          })
          .filter((id): id is string | number => typeof id === 'string' || typeof id === 'number');
        return {
          item_id: row.item_id,
          code: row.code ?? '',
          name: row.name ?? '',
          cost: Number(row.cost ?? 0),
          quantity: Number(row.quantity ?? 0),
          net_cost: Number(row.cost ?? 0),
          unit_cost: Number(row.cost ?? 0),
          discount: null,
          discount_amount: null,
          // Laravel stores item_taxes as VARCHAR(255); the legacy client sends
          // tax IDs, never the complete tax records.
          taxes: taxIds,
          allTaxes: taxIds
        };
      })
      .filter((row) => row.item_id && Number(row.quantity) > 0)
  };
}

// Stock Transfers: pending-gated (status='pending'). The SyncItemLocationStock
// listener only mutates stock for 'transferring'/'transferred' statuses.
export function normalizeTransferBeforeSave(payload: Record<string, unknown>) {
  const next = { ...payload };
  return {
    date: next.date || new Date().toISOString().slice(0, 10),
    from: next.from,
    to: next.to,
    reference: next.reference ?? '',
    details: next.details ?? '',
    // HARD GUARDRAIL: never move stock from React. Finalize (status→transferred)
    // is a deliberate action that triggers the stock movement.
    status: 'pending',
    items: (Array.isArray(next.items) ? next.items : [])
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const row = item as Record<string, unknown>;
        return {
          item_id: row.item_id,
          code: row.code ?? '',
          name: row.name ?? '',
          quantity: Number(row.quantity ?? 0)
        };
      })
      .filter((row) => row.item_id && Number(row.quantity) > 0)
  };
}

export function normalizePurchaseBeforeSave(payload: Record<string, unknown>) {
  return normalizeDraftOrderBeforeSave(payload, 'cost');
}

// Quotations have NO side effects on create (no listener, no stock/journal/payment),
// so no draft guardrail is needed. Just coerce the header + line items.
export function normalizeQuotationBeforeSave(payload: Record<string, unknown>) {
  const next = { ...payload };
  return {
    type: 'sale',
    date: next.date || new Date().toISOString().slice(0, 10),
    customer_id: next.customer_id,
    items: normalizeOrderItems(next.items, 'price')
  };
}

// RecurringSales have a draft field and the cron skips drafts (scopeActive), so
// we force draft:1 to ensure the template never auto-generates a real Sale until
// a user explicitly finalizes it in the legacy admin. No listener fires on create.
export function normalizeRecurringSaleBeforeSave(payload: Record<string, unknown>) {
  const next = { ...payload };
  return {
    type: 'sale',
    customer_id: next.customer_id,
    repeat: next.repeat || 'monthly',
    start_date: next.start_date || new Date().toISOString().slice(0, 10),
    date: next.date || new Date().toISOString().slice(0, 10),
    // draft:1 keeps this as a template only — the recurring:sales cron skips drafts.
    draft: 1,
    items: normalizeOrderItems(next.items, 'price')
  };
}

// Shared line-item coercion for order-style resources. Builds the minimal shape
// OrderDataService expects (code/name/price/quantity + the discount/taxes/selected
// keys it reads), filtering out rows without an item_id or positive quantity.
export function normalizeOrderItems(items: unknown, priceField: 'price' | 'cost') {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        item_id: row.item_id,
        code: row.code ?? '',
        name: row.name ?? '',
        [priceField]: Number(row[priceField] ?? 0),
        quantity: Number(row.quantity ?? 0),
        discount: null,
        taxes: [],
        selected: []
      };
    })
    .filter((row) => row.item_id && Number(row.quantity) > 0);
}

export function arrayRecords(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object') : [];
}

// SAFETY GUARDRAIL for accounting: incomes and expenses are ALWAYS created as
// draft (approved=0) from this form. The observer skips journal postings when
// approved is false. Payments use received=0 (observer gates on that).
export function normalizeDraftAccountingBeforeSave(payload: Record<string, unknown>, gateField: 'approved' | 'received') {
  const next = { ...payload };
  return {
    date: next.date || new Date().toISOString().slice(0, 10),
    title: next.title,
    amount: Number(next.amount ?? 0),
    account_id: next.account_id,
    reference: next.reference ?? '',
    details: next.details ?? '',
    // HARD GUARDRAIL: never approve/receive from this form.
    [gateField]: 0
  };
}

export function normalizeIncomeBeforeSave(payload: Record<string, unknown>) {
  return normalizeDraftAccountingBeforeSave(payload, 'approved');
}

export function normalizeExpenseBeforeSave(payload: Record<string, unknown>) {
  return normalizeDraftAccountingBeforeSave(payload, 'approved');
}

export function normalizePaymentBeforeSave(payload: Record<string, unknown>) {
  const next = { ...payload };
  return {
    date: next.date || new Date().toISOString().slice(0, 10),
    amount: Number(next.amount ?? 0),
    account_id: next.account_id,
    gateway: next.gateway || 'bank_transfer',
    reference: next.reference ?? '',
    // HARD GUARDRAIL: never receive from this form.
    received: 0
  };
}

export function names(value: unknown, fallback = 'None') {
  const labels = arrayRecords(value).map((item) => String(item.name ?? item.title ?? item.label ?? item.code ?? item.id ?? '')).filter(Boolean);
  return labels.length ? labels.join(', ') : fallback;
}

export function relationName(value: unknown, fallback = '-') {
  if (!value || typeof value !== 'object') return fallback;
  const record = value as Record<string, unknown>;
  return String(record.name ?? record.title ?? record.label ?? record.code ?? record.id ?? fallback);
}

export function partyName(row: CrudRecord) {
  return relationName(row.payable ?? row.customer ?? row.supplier);
}

export function categoriesSummary(row: CrudRecord) {
  return names(row.categories, 'None');
}

export function itemCategoryName(row: CrudRecord) {
  return names(row.categories, '-');
}

export function locationStockQuantity(row: CrudRecord) {
  const stock = row.location_stock as { quantity?: unknown } | undefined;
  if (stock?.quantity != null) return String(stock.quantity);
  const total = arrayRecords(row.stock).reduce((sum, entry) => sum + Number(entry.quantity ?? 0), 0);
  return total ? String(total) : '-';
}

export function itemStockSummary(row: CrudRecord) {
  const stock = arrayRecords(row.stock);
  if (!stock.length) return t('label.noStockRows');
  return stock
    .map((entry) => {
      const location = entry.location as { name?: string } | undefined;
      const locationName = location?.name ?? entry.location_id ?? 'Location';
      const parts = [
        `quantity ${entry.quantity ?? 0}`,
        `cost ${entry.cost ?? '-'}`,
        `price ${entry.price ?? '-'}`,
        `rack ${entry.rack ?? '-'}`,
        `alert ${entry.alert_quantity ?? entry.min_quantity ?? '-'}`
      ];
      return `${locationName}: ${parts.join(', ')}`;
    })
    .join('\n');
}

export function countSummary(row: CrudRecord, key: string, singular: string) {
  const count = arrayRecords(row[key]).length;
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

export function listSummary(row: CrudRecord, key: string, fallback: string, fields: string[]) {
  const records = arrayRecords(row[key]);
  if (!records.length) return fallback;
  return records
    .map((entry) => {
      const label = String(entry.name ?? entry.title ?? entry.label ?? entry.code ?? entry.sku ?? entry.id ?? 'Row');
      const details = fields.flatMap((field) => (entry[field] == null || entry[field] === '' ? [] : [`${field} ${entry[field]}`]));
      return details.length ? `${label}: ${details.join(', ')}` : label;
    })
    .join('\n');
}

export function comboPortionsSummary(row: CrudRecord) {
  const portions = arrayRecords(row.portions);
  if (!portions.length) return t('combo.noPortions');
  return portions
    .map((portion) => {
      const name = String(portion.name ?? portion.sku ?? 'Portion');
      const essentials = arrayRecords(portion.essentials)
        .map((entry) => {
          const item = entry.item as Record<string, unknown> | undefined;
          const label = String(item?.name ?? entry.name ?? entry.item_id ?? t('field.items'));
          const quantity = entry.quantity != null ? ` x${entry.quantity}` : '';
          return `${label}${quantity}`;
        });
      const groups = arrayRecords(portion.choosables)
        .map((group) => {
          const options = arrayRecords(group.items)
            .map((entry) => {
              const item = entry.item as Record<string, unknown> | undefined;
              const label = String(item?.name ?? entry.name ?? entry.item_id ?? t('field.items'));
              const quantity = entry.quantity != null ? ` x${entry.quantity}` : '';
              return `${label}${quantity}`;
            })
            .join(' / ');
          return `${group.name ?? t('combo.choice')}: ${options || t('combo.noChoices')}`;
        });
      return [
        name,
        essentials.length ? `${t('combo.essential')}: ${essentials.join(', ')}` : null,
        groups.length ? `${t('combo.choices')}: ${groups.join('; ')}` : null
      ].filter(Boolean).join('\n');
    })
    .join('\n\n');
}

export function comboCompositionCount(row: CrudRecord) {
  const portions = arrayRecords(row.portions);
  const essentials = portions.reduce((count, portion) => count + arrayRecords(portion.essentials).length, 0);
  const groups = portions.reduce((count, portion) => count + arrayRecords(portion.choosables).length, 0);
  const choices = portions.reduce((count, portion) => count + arrayRecords(portion.choosables).reduce((sum, group) => sum + arrayRecords(group.items).length, 0), 0);
  const parts = [t('combo.portionCount', { count: portions.length })];
  if (essentials) parts.push(t('combo.essentialCount', { count: essentials }));
  if (groups) parts.push(t('combo.groupCount', { count: groups }));
  if (choices) parts.push(t('combo.choiceCount', { count: choices }));
  return parts.join(', ');
}

export function itemVariantSummary(row: CrudRecord) {
  const variantText = listSummary(row, 'variants', '', ['sku', 'price', 'cost', 'quantity']);
  if (variantText) return variantText;
  return listSummary(row, 'variations', t('label.noVariants'), ['sku', 'price', 'cost', 'quantity']);
}

export function yesNoValue(value: unknown) {
  return value ? t('status.yes') : t('status.no');
}

export function moneyValue(value: unknown) {
  if (value == null || value === '') return '-';
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value);
}

export function cashDifference(row: CrudRecord) {
  const submitted = Number(row.total_cash_submitted);
  const counted = Number(row.total_cash_amount);
  if (!Number.isFinite(submitted) || !Number.isFinite(counted)) return null;
  return submitted - counted;
}

// Payment status for the sales list column. The `paid` field on the sales table
// is a boolean flag (tinyint), not an amount. POS-paid sales have paid=1.
// Drafts have paid=0. Unpaid/finalized-but-unpaid show "Unpaid".
export function paymentStatusColumn(row: CrudRecord) {
  if (row.draft) return t('status.draft');
  if (row.paid == 1 || row.paid === true) return t('status.paid');
  return t('status.unpaid');
}

export function orderItemsSummary(row: CrudRecord) {
  const items = arrayRecords(row.items);
  if (!items.length) return t('label.noLineItems');
  return items
    .map((entry) => {
      const item = entry.item as { name?: string; code?: string } | undefined;
      const name = item?.name ?? entry.name ?? entry.item_name ?? entry.code ?? entry.item_id ?? 'Item';
      return `${name}: qty ${entry.quantity ?? '-'}, total ${entry.total ?? entry.subtotal ?? '-'}`;
    })
    .join('\n');
}

// Payment summary for the detail drawer. Merges both payment collections:
//   - row.payments: the many-to-many pivot payments (web admin path)
//   - row.directPayments: the hasMany payments (POS / OrderPaymentService path)
// The SaleController::show() already merges these server-side, but we also
// check directPayments here as a fallback for sales loaded before that fix.
export function paymentSummary(row: CrudRecord) {
  const pivot = arrayRecords(row.payments);
  const direct = arrayRecords(row.directPayments);
  const payments = pivot.length ? pivot : direct;
  if (!payments.length) return t('label.noPayments');
  return payments
    .map((entry) => {
      const gateway = entry.gateway ?? entry.payment_method ?? 'unknown';
      const method = entry.payment_method && entry.payment_method !== gateway ? ` (${entry.payment_method})` : '';
      return `${entry.date ?? '-'}  ${gateway}${method}  ${moneyValue(entry.amount)}`;
    })
    .join('\n');
}

export const accountingDetailSections = [
  {
    title: 'Summary',
    fields: [
      { key: 'date', label: 'Date' },
      { key: 'number', label: 'Number' },
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'amount', label: 'Amount', render: (row: CrudRecord) => moneyValue(row.amount) },
      { key: 'account', label: 'Account', render: (row: CrudRecord) => relationName(row.account) },
      { key: 'location', label: 'Location', render: (row: CrudRecord) => relationName(row.location) },
      { key: 'user', label: 'User', render: (row: CrudRecord) => relationName(row.user) }
    ]
  },
  {
    title: 'Classification',
    fields: [
      { key: 'categories', label: 'Categories', render: categoriesSummary },
      { key: 'approved', label: 'Approved', render: (row: CrudRecord) => yesNoValue(row.approved) },
      { key: 'approved_by', label: 'Approved By', render: (row: CrudRecord) => relationName(row.approvedBy ?? row.approved_by) },
      { key: 'recurring', label: 'Recurring', render: (row: CrudRecord) => yesNoValue(row.recurring) },
      { key: 'repeat', label: 'Repeat' }
    ]
  },
  {
    title: 'Notes',
    fields: [
      { key: 'details', label: 'Details' },
      { key: 'extra_attributes', label: 'Custom Fields' }
    ]
  }
];

export const paymentDetailSections = [
  {
    title: 'Summary',
    fields: [
      { key: 'created_at', label: 'Created' },
      { key: 'number', label: 'Number' },
      { key: 'reference', label: 'Reference' },
      { key: 'amount', label: 'Amount', render: (row: CrudRecord) => moneyValue(row.amount) },
      { key: 'gateway', label: 'Gateway' },
      { key: 'received', label: 'Received', render: (row: CrudRecord) => yesNoValue(row.received) },
      { key: 'review', label: 'Review Required', render: (row: CrudRecord) => yesNoValue(row.review) }
    ]
  },
  {
    title: 'Parties',
    fields: [
      { key: 'payable', label: 'Payer / Payee', render: partyName },
      { key: 'account', label: 'Account', render: (row: CrudRecord) => relationName(row.account) },
      { key: 'location', label: 'Location', render: (row: CrudRecord) => relationName(row.location) },
      { key: 'user', label: 'User', render: (row: CrudRecord) => relationName(row.user) }
    ]
  },
  {
    title: 'Payment Details',
    fields: [
      { key: 'card_holder', label: 'Card Holder' },
      { key: 'card_number', label: 'Card Number' },
      { key: 'cheque_number', label: 'Cheque Number' },
      { key: 'gift_card_number', label: 'Gift Card Number' },
      { key: 'details', label: 'Details' }
    ]
  }
];

export const accountDetailSections = [
  {
    title: 'Account',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'reference', label: 'Reference' },
      { key: 'type', label: 'Type' },
      { key: 'opening_balance', label: 'Opening Balance', render: (row: CrudRecord) => moneyValue(row.opening_balance) },
      { key: 'journal', label: 'Journal Balance', render: (row: CrudRecord) => moneyValue((row.journal as { balance?: unknown } | undefined)?.balance) }
    ]
  },
  {
    title: 'Notes',
    fields: [{ key: 'details', label: 'Details' }]
  }
];

export const simpleDetailSections = [
  {
    title: 'Summary',
    fields: [
      { key: 'date', label: 'Date' },
      { key: 'created_at', label: 'Created' },
      { key: 'number', label: 'Number' },
      { key: 'reference', label: 'Reference' },
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'title', label: 'Title' },
      { key: 'status', label: 'Status' },
      { key: 'type', label: 'Type' },
      { key: 'amount', label: 'Amount', render: (row: CrudRecord) => moneyValue(row.amount) },
      { key: 'grand_total', label: 'Grand Total', render: (row: CrudRecord) => moneyValue(row.grand_total) },
      { key: 'balance', label: 'Balance', render: (row: CrudRecord) => moneyValue(row.balance) }
    ]
  },
  {
    title: 'Relations',
    fields: [
      { key: 'customer', label: 'Customer', render: (row: CrudRecord) => relationName(row.customer) },
      { key: 'supplier', label: 'Supplier', render: (row: CrudRecord) => relationName(row.supplier) },
      { key: 'location', label: 'Location', render: (row: CrudRecord) => relationName(row.location) },
      { key: 'user', label: 'User', render: (row: CrudRecord) => relationName(row.user) },
      { key: 'account', label: 'Account', render: (row: CrudRecord) => relationName(row.account) },
      { key: 'register', label: 'Register', render: (row: CrudRecord) => relationName(row.register) },
      { key: 'categories', label: 'Categories', render: categoriesSummary }
    ]
  },
  {
    title: 'Details',
    fields: [
      { key: 'details', label: 'Details' },
      { key: 'summary', label: 'Summary' },
      { key: 'items', label: 'Items', render: (row: CrudRecord) => countSummary(row, 'items', 'item') }
    ]
  }
];

export const partyDetailSections = [
  {
    title: 'Identity',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'company', label: 'Company' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' }
    ]
  },
  {
    title: 'Address',
    fields: [
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state_name', label: 'State' },
      { key: 'country_name', label: 'Country' },
      { key: 'postal_code', label: 'Postal Code' }
    ]
  },
  {
    title: 'Credit',
    fields: [
      { key: 'opening_balance', label: 'Opening Balance', render: (row: CrudRecord) => moneyValue(row.opening_balance) },
      { key: 'due_limit', label: 'Due Limit', render: (row: CrudRecord) => moneyValue(row.due_limit) },
      { key: 'max_due_amount', label: 'Max Due Amount', render: (row: CrudRecord) => moneyValue(row.max_due_amount) },
      { key: 'journal', label: 'Journal Balance', render: (row: CrudRecord) => moneyValue((row.journal as { balance?: unknown } | undefined)?.balance) }
    ]
  }
];

export const orderDetailSections = (party: 'customer' | 'supplier' | 'order') => [
  {
    title: 'Summary',
    fields: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'number', label: 'Number' },
      { key: party, label: party[0].toUpperCase() + party.slice(1), render: (row: CrudRecord) => String((row[party] as { name?: string; company?: string } | undefined)?.name ?? (row[party] as { company?: string } | undefined)?.company ?? '-') },
      { key: 'location', label: 'Location', render: (row: CrudRecord) => String((row.location as { name?: string } | undefined)?.name ?? '-') },
      { key: 'user', label: 'User', render: (row: CrudRecord) => String((row.user as { name?: string; username?: string } | undefined)?.name ?? (row.user as { username?: string } | undefined)?.username ?? '-') }
    ]
  },
  {
    title: 'Totals',
    fields: [
      { key: 'total_items', label: 'Items' },
      { key: 'total_quantity', label: 'Quantity' },
      { key: 'total', label: 'Total', render: (row: CrudRecord) => moneyValue(row.total) },
      { key: 'discount_amount', label: 'Discount', render: (row: CrudRecord) => moneyValue(row.discount_amount) },
      { key: 'total_tax_amount', label: 'Tax', render: (row: CrudRecord) => moneyValue(row.total_tax_amount) },
      { key: 'grand_total', label: 'Grand Total', render: (row: CrudRecord) => moneyValue(row.grand_total) },
      { key: 'paid', label: 'Payment Status', render: (row: CrudRecord) => paymentStatusColumn(row) }
    ]
  },
  {
    title: 'Line Items',
    render: orderItemsSummary
  },
  {
    title: 'Payments',
    render: paymentSummary
  },
  {
    title: 'Notes',
    fields: [{ key: 'details', label: 'Details' }]
  }
];

export const adjustmentDetailSections = [
  {
    title: 'Summary',
    fields: [
      { key: 'date', label: 'Date' },
      { key: 'type', label: 'Type' },
      { key: 'reference', label: 'Reference' },
      { key: 'number', label: 'Number' },
      { key: 'status', label: 'Status' },
      { key: 'draft', label: 'Draft', render: (row: CrudRecord) => (row.draft ? 'Yes' : 'No') },
      { key: 'location', label: 'Location', render: (row: CrudRecord) => relationName(row.location) },
      { key: 'user', label: 'User', render: (row: CrudRecord) => relationName(row.user) }
    ]
  },
  {
    title: 'Totals',
    fields: [
      { key: 'total', label: 'Total', render: (row: CrudRecord) => moneyValue(row.total) },
      { key: 'discount_amount', label: 'Discount', render: (row: CrudRecord) => moneyValue(row.discount_amount) },
      { key: 'total_tax_amount', label: 'Tax', render: (row: CrudRecord) => moneyValue(row.total_tax_amount) },
      { key: 'grand_total', label: 'Grand Total', render: (row: CrudRecord) => moneyValue(row.grand_total) }
    ]
  },
  {
    title: 'Line Items',
    render: orderItemsSummary
  },
  {
    title: 'Notes',
    fields: [{ key: 'details', label: 'Details' }]
  }
];

export const stockTransferDetailSections = [
  {
    title: 'Summary',
    fields: [
      { key: 'date', label: 'Date' },
      { key: 'status', label: 'Status' },
      { key: 'reference', label: 'Reference' },
      { key: 'number', label: 'Number' },
      { key: 'fromLocation', label: 'From', render: (row: CrudRecord) => relationName(row.fromLocation ?? row.from) },
      { key: 'toLocation', label: 'To', render: (row: CrudRecord) => relationName(row.toLocation ?? row.to) },
      { key: 'user', label: 'User', render: (row: CrudRecord) => relationName(row.user) }
    ]
  },
  {
    title: 'Line Items',
    render: orderItemsSummary
  },
  {
    title: 'Notes',
    fields: [{ key: 'details', label: 'Details' }]
  }
];

